import { useSessionStore } from "@/stores/session";
import peerManager from "@/lib/peer-manager";
// import { useProjectsStore } from "@/stores/projects";

const AUTH_TOKEN_KEY = 'mobile_auth_token';

// Extend RequestInit to include our custom option
export interface HttpOptions extends RequestInit {
  sendProjectId?: boolean;
}

async function httpViaWebRTC<T>(url: string, init?: HttpOptions): Promise<T> {
  const response = await peerManager.proxyRequest(url, init);
  
  if (!response.ok) {
    try {
      const errorData = await response.json();
      throw new Error(errorData.detail || `${response.status} ${response.statusText}`);
    } catch (e) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
  }

  if (response.status === 204) {
    // @ts-ignore
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export class HttpError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export async function http<T>(url: string, init?: HttpOptions): Promise<T> {
  const { communicationMode } = useSessionStore.getState();

  // Extract custom option and default it to true
  const { sendProjectId = true, ...restInit } = init ?? {};
  let finalUrl = url;

  if (sendProjectId) {
    const ACTIVE_PROJECT_ID_KEY = 'activeProjectId';

    const activeProjectId = localStorage.getItem(ACTIVE_PROJECT_ID_KEY);
    if (activeProjectId) {
      const separator = finalUrl.includes('?') ? '&' : '?';
      finalUrl += `${separator}project_id=${activeProjectId}`;
    }
  }

  if (communicationMode === 'webrtc') {
    return httpViaWebRTC(finalUrl, restInit);
  }

  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const { headers: initHeaders, cache } = restInit;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(initHeaders || {}),
  };

  if (token) {
    // @ts-ignore
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(finalUrl, {
    ...restInit,
    cache: cache ?? "no-store",
    headers,
  });

  if (res.status === 401 && window.location.pathname !== '/pages/onboarding/mobile-auth') {
    // Throw an error to stop the current execution flow
    throw new HttpError('Unauthorized', res.status);
  }

  if (!res.ok) {
    // Try to parse a JSON error message from the backend.
    try {
      const errorData = await res.json();
      throw new HttpError(errorData.detail || `${res.status} ${res.statusText}`, res.status);
    } catch (e) {
      if (e instanceof HttpError) throw e;
      // Fallback if the error response is not JSON.
      throw new HttpError(`${res.status} ${res.statusText}`, res.status);
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}